import { Card, CardBody, Badge } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import jobImages2 from "../../../assets/images/featured-job/img-02.png";
import { translateJobType } from "../../../utils/jobTranslations";
import { applyToVacancy } from "../../../services/applicationService";
import { useAuth } from "../../../hooks/useAuth";
import { toast } from "react-toastify";
import { useLanguage } from "../../../context/LanguageContext";

const RightSideContent = ({ job }) => {
  const { user, hasRole } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  document.title = language === "pt" ? "Detalhes da Vaga" : "Job Details";

  if (!job) return null;

  const {
    id,
    title,
    type,
    yearsOfExperience,
    degreeRequired,
    company,
    city,
    state,
    country,
    remoteAllowed,
    createdAt
  } = job;

  const getTimeSincePosting = (dateString) => {
    const postedDate = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - postedDate) / (1000 * 60 * 60));

    if (diffHours < 24)
      return `${
        language === "pt" ? "Publicado há" : "Published"
      } ${diffHours} hour${diffHours !== 1 ? "s" : ""}${
        language === "pt" ? "" : " ago"
      }`;
    const diffDays = Math.floor(diffHours / 24);
    return `${language === "pt" ? "Publicado há" : "Published"} ${diffDays} ${
      language === "pt" ? "dia" : "day"
    }${diffDays !== 1 ? "s" : ""}${language === "pt" ? "" : " ago"}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.candidateId) {
      toast.info(
        language === "pt"
          ? "Usuário não autenticado!"
          : "User not authenticated!"
      );

      setTimeout(() => {
        navigate("/signin");
      }, 1000);

      return;
    }

    try {
      const applicationDto = {
        candidateId: user.candidateId,
      };

      await applyToVacancy(id, applicationDto, token);

      toast.success(
        language === "pt"
          ? "Candidatura enviada com sucesso!"
          : "Application submitted successfully!"
      );
    } catch (err) {
      toast.error(
        err.message ||
          (language === "pt"
            ? "Erro ao enviar candidatura"
            : "Error submitting application")
      );

      if (err.message === "Perfil do candidato não está completo") {
        navigate("/myprofile");
        localStorage.setItem("activeTab", 2);

        toast.warn(
          language === "pt"
            ? "Por favor, preencha todos campos do perfil"
            : "Please, fill all fields of profile"
        );
      }
    }
  };

  return (
    <div className="side-bar ms-lg-4">
      <Card className="job-overview">
        <CardBody className="p-4">
          <h6 className="fs-17">
            {language === "pt" ? "Visão Geral da Vaga" : "Job Overview"}
          </h6>
          <ul className="list-unstyled mt-4 mb-0">
            <li>
              <div className="d-flex mt-4">
                <div className="w-4">
                  <i className="uil uil-user icon bg-primary-subtle text-primary"></i>
                </div>
                <div className="ms-3">
                  <h6 className="fs-14 mb-2">
                    {language === "pt" ? "Título da Vaga" : "Job Title"}
                  </h6>
                  <p className="text-muted mb-0">{title}</p>
                </div>
              </div>
            </li>
            <li>
              <div className="d-flex mt-4">
                <div className="w-4">
                  <i className="uil uil-star-half-alt icon bg-primary-subtle text-primary"></i>
                </div>
                <div className="ms-3">
                  <h6 className="fs-14 mb-2">
                    {language === "pt" ? "Experiência" : "Experience"}
                  </h6>
                  <p className="text-muted mb-0">
                    {yearsOfExperience === 1
                      ? yearsOfExperience +
                        " " +
                        (language === "pt" ? "Ano" : "Year")
                      : yearsOfExperience +
                        " " +
                        (language === "pt" ? "Anos" : "Years")}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className="d-flex mt-4">
                <div className="w-4">
                  <i className="uil uil-location-point icon bg-primary-subtle text-primary"></i>
                </div>
                <div className="ms-3">
                  <h6 className="fs-14 mb-2">
                    {language === "pt" ? "Localização" : "Location"}
                  </h6>
                  <p className="text-muted mb-0">
                    {city || state || country || "Remoto"}
                    {remoteAllowed && (
                      <Badge color="success" className="ms-2">
                        {language === "pt" ? "Remoto" : "Remote"}
                      </Badge>
                    )}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className="d-flex mt-4">
                <div className="w-4">
                  <i className="uil uil-graduation-cap icon bg-primary-subtle text-primary"></i>
                </div>
                <div className="ms-3">
                  <h6 className="fs-14 mb-2">
                    {language === "pt" ? "Qualificação" : "Qualification"}
                  </h6>
                  <p className="text-muted mb-0">
                    {degreeRequired ||
                      (language === "pt"
                        ? "Não especificado"
                        : "Not specified")}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className="d-flex mt-4">
                <div className="w-4">
                  <i className="uil uil-briefcase-alt icon bg-primary-subtle text-primary"></i>
                </div>
                <div className="ms-3">
                  <h6 className="fs-14 mb-2">
                    {language === "pt" ? "Tipo de Emprego" : "Job Type"}
                  </h6>
                  <p className="text-muted mb-0">
                    {translateJobType(type, language)}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className="d-flex mt-4">
                <div className="w-4">
                  <i className="uil uil-history icon bg-primary-subtle text-primary"></i>
                </div>
                <div className="ms-3">
                  <h6 className="fs-14 mb-2">
                    {language === "pt" ? "Data da Publicação" : "Date Posted"}
                  </h6>
                  <p className="text-muted mb-0">
                    {getTimeSincePosting(createdAt)}
                  </p>
                </div>
              </div>
            </li>
          </ul>
          <div className="mt-3">
            {!hasRole("ADMIN") ? (
              <Link
                to="#"
                onClick={handleSubmit}
                className="btn btn-primary btn-hover w-100 mt-2"
              >
                {language === "pt" ? "Candidatar-se" : "Apply"}{" "}
                <i className="uil uil-arrow-right"></i>
              </Link>
            ) : (
              <Link
                to="/candidatelist"
                className="btn btn-primary btn-hover w-100 mt-2"
              >
                {language === "pt" ? "Ver Candidatos" : "View Candidates"}{" "}
                <i className="uil uil-arrow-right"></i>
              </Link>
            )}
          </div>
        </CardBody>
      </Card>

      {hasRole("ADMIN") && company && (
        <Card className="company-profile mt-4">
          <CardBody className="p-4">
            <div className="text-center">
              <img
                src={company.logo || jobImages2}
                alt={company.name}
                className="img-fluid rounded-3"
                style={{ maxHeight: "80px" }}
              />

              <div className="mt-4">
                <h6 className="fs-17 mb-1">{company.name}</h6>
                <p className="text-muted">
                  {language === "pt" ? "Desde" : "Since"}{" "}
                  {company.foundedYear || "N/A"}
                </p>
              </div>
            </div>
            <ul className="list-unstyled mt-4">
              <li>
                <div className="d-flex">
                  <i className="uil uil-phone-volume text-primary fs-4"></i>
                  <div className="ms-3">
                    <h6 className="fs-14 mb-2">
                      {language === "pt" ? "Telefone" : "Phone"}
                    </h6>
                    <p className="text-muted fs-14 mb-0">
                      {company.mobileNumber || "N/A"}
                    </p>
                  </div>
                </div>
              </li>
              <li className="mt-3">
                <div className="d-flex">
                  <i className="uil uil-envelope text-primary fs-4"></i>
                  <div className="ms-3">
                    <h6 className="fs-14 mb-2">Email</h6>
                    <p className="text-muted fs-14 mb-0">
                      {company.email || "N/A"}
                    </p>
                  </div>
                </div>
              </li>
              <li className="mt-3">
                <div className="d-flex">
                  <i className="uil uil-globe text-primary fs-4"></i>
                  <div className="ms-3">
                    <h6 className="fs-14 mb-2">
                      {language === "pt" ? "Website" : "Website"}
                    </h6>
                    <p className="text-muted fs-14 text-break mb-0">
                      {company.website ? (
                        <a
                          href={company.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {company.website.replace(/^https?:\/\//, "")}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </p>
                  </div>
                </div>
              </li>
              <li className="mt-3">
                <div className="d-flex">
                  <i className="uil uil-map-marker text-primary fs-4"></i>
                  <div className="ms-3">
                    <h6 className="fs-14 mb-2">
                      {language === "pt" ? "Localização" : "Location"}
                    </h6>
                    <p className="text-muted fs-14 mb-0">
                      {[
                        company.city,
                        company.state,
                        company.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                </div>
              </li>
            </ul>
            <div className="mt-4">
              <Link
                to={`/companydetails/${company.id}`}
                className="btn btn-primary btn-hover w-100 rounded"
              >
                <i className="mdi mdi-eye"></i>{" "}
                {language === "pt" ? "Ver Perfil" : "View Details"}
              </Link>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="mt-4">
        <h6 className="fs-16 mb-3">
          {language === "pt" ? "Localização da Vaga" : "Job Location"}
        </h6>
        <iframe
          title="EP Management and Consultancy"
          src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d15985.123456789012!2d32.5863954!3d-25.9523387!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ee69bac7dce465b%3A0x8b4bf7461e26c1e7!2sEP%20Management%20%26%20Consultancy%20Services!5e0!3m2!1spt-PT!2smz!4v1713262686123!5m2!1spt-PT!2smz"
          style={{ width: `100%`, height: `250px` }}
          allowFullScreen=""
          loading="lazy"
        ></iframe>
      </div>
    </div>
  );
};

export default RightSideContent;
